import { For, Show, createEffect, createMemo, createSignal, onCleanup } from 'solid-js'

import { useI18n } from '../../i18n'
import { formatDuration, formatTargetHours } from '../../state/currentFastStore'
import { getEntryTimestampMs } from '../../state/measurementsStore'
import {
   measurementsPage,
   measurementsRangeFilter,
   setMeasurementsPage,
   setMeasurementsRangeFilter,
} from '../../state/measurementsUiStore'
import {
   SLEEP_TARGET_HOURS,
   SLEEP_TARGET_MS,
   completedSleeps,
   endSleep,
   getSleepEndMs,
   openSleepEditModal,
   sleepEntries,
   sleepInProgress,
   startSleep,
} from '../../state/sleepStore'
import { getRangeTimestamps } from '../../utils/timeRange'
import ProgressContainer from '../current-fast/ProgressContainer'
import RangeFilter from '../measurements/RangeFilter'
import VolumeBarChart from '../measurements/VolumeBarChart'
import { buildVolumeBuckets, getBucketSize } from '../measurements/metricViewUtils'
import EditIconButton from '../ui/EditIconButton'
import {
   atWakeUpTime,
   averageBuckets,
   formatClockTime,
   formatSleepLength,
   formatSleepSpan,
} from './sleepFormat'

type Translate = ReturnType<typeof useI18n>

const ENTRIES_PER_PAGE = 5
const SLEEP_TARGET_SECONDS = SLEEP_TARGET_HOURS * 60 * 60
const RING_FILL = '#818cf8'
const RING_TRACK = 'var(--progress-track-blue, #0f172a)'

/**
 * Builds the ring's conic gradient for a completion ratio.
 *
 * @param ratio - Completion ratio; values past 1 fill the ring.
 * @returns The `conic-gradient(...)` CSS value.
 */
function ringBackground(ratio: number) {
   const angle = Math.max(0, Math.min(1, ratio)) * 360
   return `conic-gradient(${RING_FILL} 0deg ${angle}deg, ${RING_TRACK} ${angle}deg 360deg)`
}

/**
 * Labels for the sleep ring: the running sleep against the target, otherwise the last
 * ended sleep.
 *
 * @param now - The ticking clock.
 * @param remaining - Whether to count down to the target instead of up.
 * @param t - The translation function.
 * @returns The label memos.
 */
function createSleepRingLabels(
   now: () => number,
   remaining: () => boolean,
   t: Translate,
) {
   const lastSleep = createMemo(() => completedSleeps()[0] ?? null)
   const elapsedMs = createMemo(() => {
      const running = sleepInProgress()
      if (running) {
         return Math.max(0, now() - getEntryTimestampMs(running))
      }
      const last = lastSleep()
      return last ? (last.values.duration ?? 0) * 1000 : 0
   })
   const ratio = createMemo(() => elapsedMs() / SLEEP_TARGET_MS)
   const progressLabel = createMemo(() =>
      sleepInProgress() || !lastSleep() ?
         t('Progress percentage label', {
            percentage: Math.floor(ratio() * 100),
            target: formatTargetHours(SLEEP_TARGET_MS),
         })
      :  t('Last sleep'),
   )
   const timerLabel = createMemo(() =>
      formatDuration(
         sleepInProgress() && remaining() ?
            Math.max(0, SLEEP_TARGET_MS - elapsedMs())
         :  elapsedMs(),
      ),
   )
   const detailTitle = createMemo(() => {
      if (sleepInProgress()) {
         return t('Wake-up target')
      }
      return lastSleep() ? t('Woke up') : ''
   })
   const detailLabel = createMemo(() => {
      const running = sleepInProgress()
      if (running) {
         return formatClockTime(getEntryTimestampMs(running) + SLEEP_TARGET_MS)
      }
      const last = lastSleep()
      const endMs = last ? getSleepEndMs(last) : null
      return endMs === null ? '' : formatClockTime(endMs)
   })
   return { ratio, progressLabel, timerLabel, detailTitle, detailLabel }
}

/**
 * Renders the sleep ring with the start/stop button.
 *
 * @returns The timer section.
 */
const SleepTimer = () => {
   const t = useI18n()
   const [now, setNow] = createSignal(Date.now())
   const [remaining, setRemaining] = createSignal(false)
   const labels = createSleepRingLabels(now, remaining, t)

   createEffect(() => {
      if (!sleepInProgress()) {
         return
      }
      const intervalId = window.setInterval(() => setNow(Date.now()), 1000)
      onCleanup(() => window.clearInterval(intervalId))
   })

   return (
      <section
         id="sleep-timer"
         class="flex flex-col items-center gap-2 pb-4 lg:p-4 lg:bg-surface-raised/70 lg:rounded-xl lg:border lg:border-edge-soft"
      >
         <ProgressContainer
            idPrefix="sleep-"
            active={sleepInProgress() !== null}
            displayMode={remaining() ? 'remaining' : 'elapsed'}
            fatBurnLabel={labels.detailLabel()}
            fatBurnTitle={labels.detailTitle()}
            onToggleDisplayMode={() => setRemaining(!remaining())}
            progressBackground={ringBackground(labels.ratio())}
            progressLabel={labels.progressLabel()}
            syncLoading={false}
            timerLabel={labels.timerLabel()}
            toggleLabel={t('Toggle timer display mode')}
         />
         <button
            id="sleep-toggle-btn"
            type="button"
            onClick={() => {
               setNow(Date.now())
               if (sleepInProgress()) {
                  endSleep()
               } else {
                  startSleep()
               }
            }}
            class="btn-accent w-full max-w-xs px-6 py-3 text-lg font-semibold text-white rounded-lg transition"
         >
            {sleepInProgress() ? t('End Sleep') : t('Start Sleep')}
         </button>
      </section>
   )
}

/**
 * Renders the paginated sleep list with edit/delete actions.
 *
 * @param props - Sleeps to list, newest first.
 * @returns The list element.
 */
const SleepList = (props: { entries: ReturnType<typeof sleepEntries> }) => {
   const t = useI18n()
   const pageCount = createMemo(() =>
      Math.max(1, Math.ceil(props.entries.length / ENTRIES_PER_PAGE)),
   )
   const currentPage = createMemo(() => Math.min(measurementsPage(), pageCount() - 1))
   const pagedEntries = createMemo(() => {
      const start = currentPage() * ENTRIES_PER_PAGE
      return props.entries.slice(start, start + ENTRIES_PER_PAGE)
   })

   return (
      <div>
         <div id="sleep-entry-list" class="space-y-2">
            <Show
               when={pagedEntries().length > 0}
               fallback={
                  <p class="text-sm text-ink-faint">
                     {t('No entries available for this period.')}
                  </p>
               }
            >
               <For each={pagedEntries()}>
                  {entry => (
                     <div class="flex items-center justify-between gap-3 py-2 lg:px-3 lg:bg-surface-raised lg:border lg:border-edge-soft lg:rounded-lg">
                        <div>
                           <p class="text-sm font-semibold text-ink">
                              {entry.values.duration === undefined ?
                                 t('In progress')
                              :  formatSleepLength(entry.values.duration)}
                           </p>
                           <p class="text-xs text-ink-faint">{formatSleepSpan(entry)}</p>
                        </div>
                        <EditIconButton
                           onClick={() => openSleepEditModal(entry.entryId)}
                        />
                     </div>
                  )}
               </For>
            </Show>
         </div>
         <Show when={props.entries.length > ENTRIES_PER_PAGE}>
            <div class="flex justify-end gap-2 mt-4 text-xs">
               <button
                  type="button"
                  onClick={() => setMeasurementsPage(currentPage() - 1)}
                  disabled={currentPage() === 0}
                  class="px-2 py-1 rounded-md border border-edge bg-surface-raised text-ink-soft disabled:opacity-50 disabled:cursor-not-allowed"
               >
                  {t('Prev')}
               </button>
               <button
                  type="button"
                  onClick={() => setMeasurementsPage(currentPage() + 1)}
                  disabled={currentPage() >= pageCount() - 1}
                  class="px-2 py-1 rounded-md border border-edge bg-surface-raised text-ink-soft disabled:opacity-50 disabled:cursor-not-allowed"
               >
                  {t('Next')}
               </button>
            </div>
         </Show>
      </div>
   )
}

/**
 * Renders the sleep metric in the measurements tab: a timer ring like the fast's,
 * average sleep per day/week/month against the target, and the list of sleeps.
 *
 * @returns The view element.
 */
const SleepView = () => {
   const t = useI18n()
   const sleepsAtWakeUp = createMemo(() => atWakeUpTime(completedSleeps()))
   const range = createMemo(() =>
      getRangeTimestamps(
         measurementsRangeFilter(),
         sleepsAtWakeUp().map(getEntryTimestampMs),
      ),
   )
   const bucketSize = createMemo(() => getBucketSize(measurementsRangeFilter()))
   const buckets = createMemo(() =>
      averageBuckets(
         buildVolumeBuckets(
            sleepsAtWakeUp(),
            entry => entry.values.duration ?? null,
            range(),
            bucketSize(),
         ),
      ),
   )
   const listedEntries = createMemo(() =>
      sleepEntries().filter(entry => {
         const timestamp = getSleepEndMs(entry) ?? getEntryTimestampMs(entry)
         return timestamp >= range().rangeStart && timestamp <= range().rangeEnd
      }),
   )
   const chartLabel = createMemo(() =>
      bucketSize() === 'day' ?
         t('Sleep per night')
      :  t('Average sleep per {bucket}', { bucket: t(bucketSize()) }),
   )

   return (
      <div id="sleep-view" class="space-y-4">
         <SleepTimer />
         <section class="lg:h-full pb-4 space-y-4 bg-transparent lg:p-4 lg:bg-surface-raised/70 lg:rounded-xl lg:border lg:border-edge-soft">
            <h2 class="text-xl font-semibold text-ink">{t('History')}</h2>
            <RangeFilter
               id="sleep-filter-container"
               value={measurementsRangeFilter()}
               onChange={setMeasurementsRangeFilter}
            />
            <div class="space-y-2">
               <VolumeBarChart
                  buckets={buckets()}
                  bucketSize={bucketSize()}
                  formatTotal={total =>
                     formatTargetHours(Math.round(total / 360) * 360_000)
                  }
                  formatTooltip={formatSleepLength}
                  label={chartLabel()}
                  target={SLEEP_TARGET_SECONDS}
                  targetLabel={t('Target')}
                  splitAt={SLEEP_TARGET_SECONDS}
               />
               <p class="text-xs text-ink-faint">
                  {chartLabel()} ·{' '}
                  {t('Target {value} per {bucket}', {
                     value: formatTargetHours(SLEEP_TARGET_MS),
                     bucket: t('night'),
                  })}
               </p>
            </div>
            <SleepList entries={listedEntries()} />
         </section>
      </div>
   )
}

export default SleepView
